import { useId } from 'react';
import type { ReactNode } from 'react';

import { cx } from '../lib/cx.ts';
import { ModalBase } from './ModalBase.tsx';

type SheetProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  /**
   * `nested` marks a sheet that edits something *inside* whatever is behind it
   * — a procedure within a case. It is a lighter surface with an accent edge,
   * so it is immediately obvious that these fields belong to the procedure and
   * not to the case still visible underneath.
   */
  tone?: 'default' | 'nested';
};

/**
 * A bottom sheet on a phone, a centred panel on a wide screen. Used for the
 * procedure picker and anything else that needs room without losing the
 * context behind it.
 */
export function Sheet({ open, onClose, title, children, footer, tone = 'default' }: SheetProps) {
  const titleId = useId();

  return (
    <ModalBase
      open={open}
      onClose={onClose}
      className="mt-auto mb-0 w-full rail:m-auto rail:w-auto"
    >
      <div
        role="document"
        aria-labelledby={titleId}
        className={cx(
          'flex max-h-[85dvh] w-full flex-col rounded-t-xl border shadow-lg',
          'rail:w-[min(34rem,calc(100dvw-4rem))] rail:rounded-xl',
          tone === 'nested' ? 'border-accent/40 bg-surface-raised' : 'border-line bg-surface',
        )}
      >
        <header
          className={cx(
            'flex items-center justify-between gap-3 border-b px-5 py-4',
            tone === 'nested' ? 'border-accent/30 bg-accent-quiet' : 'border-line',
          )}
        >
          <h2 id={titleId} className="text-base font-strong">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="grid size-touch place-items-center rounded-md text-muted hover:bg-surface-raised hover:text-text"
          >
            <span aria-hidden="true" className="text-xl leading-none">
              ×
            </span>
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-5">{children}</div>

        {footer === undefined ? null : (
          <footer className="border-t border-line px-5 py-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
            {footer}
          </footer>
        )}
      </div>
    </ModalBase>
  );
}
